/** Artboard 4 · Gastos — the report list (file kept as Viajes; the strings say Gastos / reporte). */
import { Link, useSearchParams } from 'react-router-dom';
import { call } from '../api/client.ts';
import type { TripSummary } from '../api/types.ts';
import { useLoad } from '../state/hooks.ts';
import { attentionCopy, BUCKET_LABEL, BUCKET_TONE } from '../state/view.ts';
import { dateRange, money, plural } from '../lib/format.ts';
import { AppHeader, Banner, Button, Count, ErrorNote, Icon, Loading } from '../theme/ui.tsx';
import Inicio from './Inicio.tsx';
import { useProfile } from './Perfil.tsx';
import { InstallCard } from './Instalar.tsx';
import type { Session } from '../App.tsx';

export function TripCounts({ t }: { t: TripSummary }) {
  const c = t.counts;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {c.conFactura > 0 && <Count n={c.conFactura} label={BUCKET_LABEL.con_factura} tone={BUCKET_TONE.con_factura} />}
      {c.pendientes > 0 && <Count n={c.pendientes} label={BUCKET_LABEL.pendientes} tone={BUCKET_TONE.pendientes} />}
      {c.sinFactura > 0 && <Count n={c.sinFactura} label={BUCKET_LABEL.sin_factura} tone={BUCKET_TONE.sin_factura} />}
    </div>
  );
}
export const tripMeta = (t: TripSummary) => [dateRange(t.dateFrom, t.dateTo), plural(t.receiptCount, 'comprobante', 'comprobantes')].filter(Boolean).join(' · ');

export default function Viajes({ session }: { session: Session }) {
  const [sp] = useSearchParams();
  const showArchived = sp.get('archivados') === '1';
  const active = useLoad(() => call('trips.list', { query: { archived: 'false' } }), []);
  const archived = useLoad(() => call('trips.list', { query: { archived: 'true' } }), []);
  const attention = useLoad(() => call('attention.list'), []);
  const me = useLoad(() => call('me.get'), []);
  const [profile, openProfile] = useProfile(session);

  if (active.loading || archived.loading || me.loading) return <div className="screen"><Loading /></div>;
  if (active.error) return <div className="screen"><ErrorNote error={active.error} retry={active.reload} /></div>;
  // first use: the legal entity comes before the first report (POST /trips answers 409 entity-required until it is set)
  if (me.data && me.data.entityId === null) return <Inicio session={session} needsEntity onEntityChosen={me.reload} />;
  const trips = active.data?.trips ?? []; const arch = archived.data?.trips ?? [];
  if (trips.length === 0 && arch.length === 0) return <Inicio session={session} />;
  const items = attention.data?.items ?? [];
  const list = showArchived ? arch : trips;

  return (
    <div className="screen">
      <div className="phone">
        <AppHeader name={session.displayName} onProfile={openProfile} />
        <InstallCard />
        {items.length > 0 && !showArchived && (
          <div style={{ margin: '0 20px 20px' }}>
            <Banner tone="amber" to="/atencion" icon={<Icon.Warning color="var(--amber)" width={1.8} />} right={<Icon.Chevron size={18} color="var(--amber)" />}
              title={`${plural(items.length, 'comprobante necesita', 'comprobantes necesitan')} tu atención`}
              body={summarise(items.map((i) => attentionCopy(String(i.reasonCode), i.source).title))} />
          </div>
        )}
        <div className="label" style={{ padding: '0 20px 10px' }}>{showArchived ? 'Archivados' : 'Activos'}</div>
        <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {list.length === 0 && <div className="sub" style={{ padding: '8px 0' }}>{showArchived ? 'No hay reportes archivados.' : 'No hay reportes activos.'}</div>}
          {list.map((t) => (
            <Link key={t.tripId} to={`/reporte/${t.tripId}`} className="card card--pad card--link" style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div className="grow"><div className="h4">{t.name}</div><div className="sub sub--sm" style={{ marginTop: 4 }}>{tripMeta(t)}</div></div>
                <div className="amount" style={{ fontSize: 16 }}>{money(t.totalAmount)}</div>
              </div>
              <TripCounts t={t} />
            </Link>
          ))}
        </div>
        <div style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {showArchived
            ? <Link to="/" className="btn btn--ghost btn--inline" style={{ justifyContent: 'flex-start', gap: 8 }}><Icon.Back size={17} color="var(--ink-2)" width={1.8} />Activos <span className="mono" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{trips.length}</span></Link>
            : <Link to="/?archivados=1" className="btn btn--ghost btn--inline" style={{ justifyContent: 'flex-start', gap: 8 }}><Icon.Archive size={17} color="var(--ink-2)" width={1.8} />Archivados <span className="mono" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{arch.length}</span></Link>}
          <Link to="/datos-fiscales" className="btn btn--ghost btn--inline" style={{ justifyContent: 'flex-start', gap: 8 }}><Icon.Qr size={17} color="var(--ink-2)" width={1.8} />Datos de facturación</Link>
          {session.isOperator && <Link to="/operador" className="btn btn--ghost btn--inline" style={{ justifyContent: 'flex-start', gap: 8 }}><Icon.Inbox size={17} color="var(--ink-2)" width={1.8} />Cola del operador</Link>}
        </div>
        <div className="footer"><Button to="/nuevo" icon={<Icon.Plus size={19} />}>Nuevo reporte</Button></div>
      </div>
      {profile}
    </div>
  );
}

function summarise(titles: string[]): string {
  const counts = new Map<string, number>(); for (const t of titles) counts.set(t, (counts.get(t) ?? 0) + 1);
  return [...counts].map(([t, n]) => (n > 1 ? `${n} × ${t.toLowerCase()}` : t)).join(' · ');
}
